import { DEFAULT_TIMEBAND } from "../consts/strings";
import {
  processRoutes,
  processRoutesProfile,
  isRedirectionRouteCode,
} from "./routes";
import { groupSectors, groupPostcodes } from "./lookUpTables";
import { copyOverlayDetails } from "./volumeData";

export const loadDefaultTypes = async ({
  routes,
  matrixProfile,
  volumeData,
  lookupTables,
  planType,
  depotCollections,
}) => {
  routes = processRoutes(routes, matrixProfile.routeConfig);

  const overlayData = volumeData && {
    overlayProfile: { ...volumeData.overlayProfile },
    overlayDetails: copyOverlayDetails(volumeData.overlayDetails),
  };

  const {
    routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  } = await processRoutesProfile({
    routeConfig: matrixProfile.routeConfig,
    redirectConfig: matrixProfile.redirectConfig,
    routes,
    volumeData: overlayData,
    overrideAreas: matrixProfile.overrideConfig,
    pickupAreas: matrixProfile.pickupConfig,
    locationConfig: matrixProfile.locationConfig,
    lookupTables,
    planType,
  });

  return {
    ...matrixProfile,
    routes: routesWithVolume,
    depotCollections,
    overrideAreas: matrixProfile.overrideConfig,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    volumeData: processedVolumeData,
  };
};

export const getAreasWaveSplit = ({
  routes,
  routesTimes,
  routesByOuter,
  lookupTables,
}) => {
  routes = routes.reduce((acc, route) => {
    acc[route.routeCode] = route;
    return acc;
  }, {});

  const unallocatedOuters = [];
  const unallocatedSectors = [];
  const unallocatedPostcodes = [];
  const singleWaveOuters = [];
  const singleWaveSectors = [];
  const singleWavePostcodes = [];

  Object.keys(lookupTables.OUTER).forEach((outer) => {
    const routesForOuter = Array.from(routesByOuter.D[outer] || []).filter(
      (routeCode) => !isRedirectionRouteCode(routeCode)
    );
    const waveSplit = getOuterAllocationWaveSplit({
      outer,
      routes,
      routesTimes,
      routesForOuter,
      lookupTables,
    });
    if (waveSplit.isUnallocated)
      unallocatedOuters.push({ outer, from: waveSplit.lastReady });
    if (waveSplit.isSingleWave) singleWaveOuters.push({ outer });

    if (waveSplit.unallocatedSectors.length)
      waveSplit.unallocatedSectors.forEach(([sector, from]) =>
        unallocatedSectors.push({ sector, from })
      );
    if (waveSplit.singleWaveSectors.length)
      waveSplit.singleWaveSectors.forEach((sector) =>
        singleWaveSectors.push({ sector })
      );

    if (waveSplit.unallocatedPostcodes.length)
      waveSplit.unallocatedPostcodes.forEach(([postcode, from]) =>
        unallocatedPostcodes.push({ postcode, from })
      );
    if (waveSplit.singleWavePostcodes.length)
      waveSplit.singleWavePostcodes.forEach((postcode) =>
        singleWavePostcodes.push({ postcode })
      );
  });

  return {
    unallocatedAreas: unallocatedOuters
      .concat(unallocatedSectors)
      .concat(unallocatedPostcodes),
    singleWaveAreas: singleWaveOuters
      .concat(singleWaveSectors)
      .concat(singleWavePostcodes),
  };
};

const getOuterAllocationWaveSplit = ({
  outer,
  routes,
  routesTimes,
  routesForOuter,
  lookupTables,
}) => {
  const sectors = groupSectors(lookupTables.SECTOR)[outer];
  let unallocatedSectors = {};
  let unallocatedPostcodes = sectors.reduce(
    (acc, sector) => ({ ...acc, [sector]: {} }),
    {}
  );

  let singleWaveSectors = {};
  let singleWavePostcodes = sectors.reduce(
    (acc, sector) => ({ ...acc, [sector]: {} }),
    {}
  );

  let lastReady;
  const lastReadyBySector = {};

  let totalPostcodes = 0;
  sectors.forEach((sector) => {
    const postcodes = groupPostcodes(lookupTables.POSTCODE)[sector];
    totalPostcodes += postcodes.length;

    postcodes.forEach((postcode) => {
      const routesForPostcode = routesForOuter.filter(
        (routeCode) => routesTimes[routeCode].postcode[postcode]
      );

      let postcodeLastReady = null;
      let isUnallocatedPostcode = true;
      const isSingleWavePostcode = routesForPostcode.length === 1;

      routesForPostcode.forEach((routeCode) => {
        let { plannedReadyTimeAsString } = routes[routeCode];
        if (!plannedReadyTimeAsString) {
          isUnallocatedPostcode = false;
          plannedReadyTimeAsString = DEFAULT_TIMEBAND;
        }
        if (!postcodeLastReady || postcodeLastReady < plannedReadyTimeAsString)
          postcodeLastReady = plannedReadyTimeAsString;
      });

      if (lastReady === undefined || lastReady > postcodeLastReady)
        lastReady = postcodeLastReady;
      if (
        lastReadyBySector[sector] === undefined ||
        lastReadyBySector[sector] > postcodeLastReady
      )
        lastReadyBySector[sector] = postcodeLastReady;

      if (isUnallocatedPostcode)
        unallocatedPostcodes[sector][postcode] = postcodeLastReady;
      if (isSingleWavePostcode) singleWavePostcodes[sector][postcode] = true;
    });

    if (Object.keys(unallocatedPostcodes[sector]).length === postcodes.length)
      unallocatedSectors[sector] = lastReadyBySector[sector];
    if (Object.keys(singleWavePostcodes[sector]).length === postcodes.length)
      singleWaveSectors[sector] = true;
  });

  const isUnallocated =
    totalPostcodes ===
    Object.keys(unallocatedPostcodes).reduce(
      (acc, sector) => acc + Object.keys(unallocatedPostcodes[sector]).length,
      0
    );
  const isSingleWave =
    totalPostcodes ===
    Object.keys(singleWaveSectors).reduce(
      (acc, sector) => acc + Object.keys(singleWavePostcodes[sector]).length,
      0
    );

  singleWavePostcodes = !isSingleWave
    ? Object.keys(singleWavePostcodes)
        .filter((sector) => singleWaveSectors[sector] === undefined)
        .flatMap((sector) => Object.keys(singleWavePostcodes[sector]))
    : [];
  unallocatedPostcodes = !isUnallocated
    ? Object.keys(unallocatedPostcodes)
        .filter((sector) => unallocatedSectors[sector] === undefined)
        .flatMap((sector) => Object.entries(unallocatedPostcodes[sector]))
    : [];

  singleWaveSectors = !isSingleWave ? Object.keys(singleWaveSectors) : [];
  unallocatedSectors = !isUnallocated ? Object.entries(unallocatedSectors) : [];

  return {
    lastReady,
    isUnallocated,
    isSingleWave,
    unallocatedSectors,
    singleWaveSectors,
    unallocatedPostcodes,
    singleWavePostcodes,
  };
};
